import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../lib/api";

const SASHA_AVATAR = `${process.env.REACT_APP_BACKEND_URL}/api/assets/sasha-avatar?v=2026-06-29a`;

// Pages where the floating bubble must HIDE:
//   • The full /talk-to-sasha chat — otherwise you see two Sashas.
//   • Admin consoles — internal pages, no need for a guest-facing bubble.
//   • The intake form — already a focused conversion path.
const HIDE_ON = [
  /^\/talk-to-sasha(\/|$|\?)/i,
  /^\/admin(\/|$)/i,
  /^\/concierge(\/|$)/i,
  /^\/portal(\/|$)/i,
  /^\/host(-signin|$|\/)/i,
];

/**
 * FloatingSashaBubble — site-wide info-desk chat widget.
 *
 * The bubble is the small Sasha portrait in the bottom-right corner. Click it
 * to open a 380×560 mini-chat panel. The brain on the other side is the
 * `persona="info"` Sasha — short replies, FAQs about Janté, and clean handoff
 * tags ([[GOTO:/path]]) that we honour with a route change.
 *
 * The widget hides on the full-page Sasha chat and admin/portal pages so
 * users never see two Sashas at once.
 */
export default function FloatingSashaBubble() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      cid: "welcome",
      role: "ai",
      text:
        "Hi! I'm Sasha — Janté's welcome desk. Ask me about memberships, hosting tiers, JT.Essentials, or anything else. For full trip planning I'll hand you to my travel desk.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(
    () => `bubble-${Math.random().toString(36).slice(2, 11)}`,
  );
  const scrollRef = useRef(null);

  // Hide on certain pages.
  const hide = HIDE_ON.some((rx) => rx.test(location.pathname));

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Route-jump handler — when Sasha emits [[GOTO:/path]] we navigate.
  const handleHandoffTag = (text) => {
    const m = text.match(/\[\[GOTO:([^\]]+)\]\]/);
    if (!m) return { cleaned: text, goto: null };
    return { cleaned: text.replace(m[0], "").trim(), goto: m[1] };
  };

  const send = async (raw) => {
    const t = (raw ?? input).trim();
    if (!t || busy) return;
    setMessages((m) => [...m, { cid: `u-${Date.now()}`, role: "user", text: t }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/chat/sasha", {
        session_id: sessionId,
        text: t,
        persona: "info",
        mode: "explore",
        language: "en",
      });
      const reply = r.data.reply || "(One moment…)";
      const { cleaned, goto } = handleHandoffTag(reply);
      setMessages((m) => [...m, { cid: `a-${Date.now()}`, role: "ai", text: cleaned }]);
      if (goto) {
        setTimeout(() => {
          setOpen(false);
          navigate(goto);
        }, 1200);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          cid: `err-${Date.now()}`,
          role: "ai",
          text: "I'm briefly unavailable — please try again or call +1 445 45Jante (52683).",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  if (hide) return null;

  return (
    <>
      {/* Open chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[80vh] bg-[var(--ivory)] border border-[var(--gold)]/40 shadow-2xl rounded-lg flex flex-col"
          data-testid="floating-sasha-panel"
          style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[var(--gold)]/20 flex items-center gap-3 bg-[var(--emerald)] text-[var(--ivory)] rounded-t-lg">
            <img
              src={SASHA_AVATAR}
              alt="Sasha"
              className="w-9 h-9 rounded-full object-cover ring-2 ring-[var(--gold)]/60"
            />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-tight">Sasha</div>
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">Welcome desk · online</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 hover:opacity-80"
              data-testid="floating-sasha-close"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m) => (
              <div
                key={m.cid}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[var(--emerald)] text-[var(--ivory)] rounded-l-lg rounded-tr-lg"
                      : "bg-white text-[var(--emerald)] border border-[var(--gold)]/20 rounded-r-lg rounded-tl-lg"
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "15px" }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--gold)]/20 px-3 py-2 text-sm text-[var(--muted)] italic rounded-r-lg rounded-tl-lg">
                  Sasha is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[var(--gold)]/20 p-3 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about memberships, hosting, JT.Essentials…"
              className="flex-1 bg-transparent border border-[var(--emerald)]/20 px-3 py-2 text-sm text-[var(--emerald)] focus:outline-none focus:border-[var(--gold)]"
              data-testid="floating-sasha-input"
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="p-2 bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90 disabled:opacity-40"
              data-testid="floating-sasha-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[var(--emerald)] text-[var(--ivory)] shadow-2xl flex items-center justify-center hover:scale-105 transition-transform group"
          data-testid="floating-sasha-bubble"
          aria-label="Open Sasha welcome desk"
          style={{ boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }}
        >
          <img
            src={SASHA_AVATAR}
            alt=""
            className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--gold)]"
          />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[var(--brand-red)] rounded-full ring-2 ring-[var(--ivory)]" />
          <span className="absolute right-full mr-3 whitespace-nowrap bg-[var(--emerald)] text-[var(--ivory)] text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded">
            Ask Sasha
          </span>
        </button>
      )}
    </>
  );
}
